import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'oneparacon.sidebar.collapsed';

/**
 * Sidebar state, persisted so a user's preference survives a reload.
 *
 * Three surfaces share it:
 *  - desktop (>= lg): collapsed 72px rail, or expanded 288px sidebar
 *  - below lg: sidebar is off-canvas and `mobileOpen` drives the drawer
 */
export function useSidebar() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  // Close the mobile drawer when the viewport grows past the breakpoint,
  // otherwise it stays mounted and traps scroll behind the desktop layout.
  useEffect(() => {
    const query = window.matchMedia('(min-width: 1024px)');
    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) setMobileOpen(false);
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [mobileOpen]);

  // Escape closes the drawer.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen]);

  return {
    collapsed,
    setCollapsed,
    toggleCollapsed: useCallback(() => setCollapsed((value) => !value), []),
    mobileOpen,
    setMobileOpen,
    openMobile: useCallback(() => setMobileOpen(true), []),
    closeMobile: useCallback(() => setMobileOpen(false), []),
  };
}

export type SidebarState = ReturnType<typeof useSidebar>;
